/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
// components
import Button, { ResetButton, SearchButton, CloseButton } from 'shared/buttons';
import FilterComponent from './FilterComponent';
import SortSelect from './SortSelect';
import MultiSortSelect from './MultiSortSelect';
// Styles
import './style.scss';
import { theme } from 'src/theme';
import { BaseHotKeys } from 'shared/inputs/BaseHotKeys';
import isEqual from 'lodash/isEqual';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const defaultIntialValues = {};
const defaultDefaultValues = {};

const GridFiltersBar = ({
  title,
  subtitle,
  // Initial value
  searchParams = defaultIntialValues,
  // Default value - used to reset
  defaultSearchParams = defaultDefaultValues,
  // List of Filters components
  filterFields,
  // Handlers
  autoSearch = true,
  // Search updates searchParams out of this component
  onSearch,
  onReset,
  defaultExpanded = false,
  allowReset = true,
  // Position
  isOnSide = false,
  togglePosition,
  // Sorting
  sortable = false,
  sortByList,
  onSortChange,
  sort,
  defaultSort,
  isMultiSorting = false,
  // Actions
  actionsContent = null,
  // Rest
  disabled,
  children,
  className,
  fieldsExtraStart,
  fieldsExtraEnd,
  ...props
}) => {
  // Inner value - controls here, used for debouncing
  const [values, setValues] = useState(searchParams);

  // True when any filter value has been changed
  const [modified, setModified] = useState(false);

  const resetRuns = useRef(false);

  useEffect(() => {
    if (searchParams && !isEqual(values, searchParams)) {
      setModified(false);
      setValues((prev) => ({
        ...prev, ...searchParams,
      }));
    } else if (!searchParams) {
      setModified(false);
      setValues(defaultSearchParams);
    }
  }, [searchParams]);

  // advanced search
  const [showAdvanced, setShowAdvanced] = useState(false);
  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);
  const hasAdvanced = filterFields?.find(({ isAdvanced }) => isAdvanced);

  const [expanded, setExpanded] = useState(defaultExpanded);

  const onChangeHandler = (name, debounceTimer) => (value) => {
    setModified(true);
    setValues((prev) => ({
      ...prev, ...searchParams, [name]: value, debounceTimer,
    }));
  };

  const handleSearch = (needCollapse = false) => {
    if (!modified) {
      return;
    }
    // Exclude debounceTimer
    const { debounceTimer, ...rest } = values;
    onSearch(rest);
    if (needCollapse) {
      setExpanded(defaultExpanded);
      setShowAdvanced(false);
    }
  };

  // Debounce for the filter change, not for the search itself, as this component doesn't handle the search
  const debounceSearch = useCallback(
    debounce((filtersValues) => {
      // Exclude debounceTimer
      const { debounceTimer, ...rest } = filtersValues;
      onSearch(rest);
    }, values.debounceTimer ? values.debounceTimer : 500),
    [values.debounceTimer, onSearch],
  );

  useEffect(() => {
    if (resetRuns.current) {
      resetRuns.current = false;
      return;
    }

    if (autoSearch && modified) {
      debounceSearch(values);
    }
  }, [values, autoSearch]);

  const handleReset = () => {
    resetRuns.current = true;
    setModified(false);
    onReset();
    setExpanded(defaultExpanded);
    setValues(defaultSearchParams);
  };

  const barClassName = cn('grid-toolbar', { [className]: className });
  const filterClassName = cn('grid-filters', { expanded });
  const actionsClassName = cn('filters-actions', { expanded });

  const hotKeyMap = {
    RESET_FILTERS: 'alt+f',
  };

  const handleHotKeyResetFilter = (e) => {
    e?.preventDefault();
    if (allowReset) {
      handleReset();
    }
  };

  const hotKeyHandlers = {
    RESET_FILTERS: handleHotKeyResetFilter,
  };

  return (
    <BaseHotKeys
      keyMap={hotKeyMap}
      handlers={hotKeyHandlers}
    >
      <div className={barClassName} {...props}>
        <div className='top'>
          {title && <div className='grid-toolbar-title'>{title}</div>}
          {subtitle && <div className='grid-toolbar-subtitle'>{subtitle}</div>}
          {filterFields?.length > 0 && (
            <div className={actionsClassName}>
              {(sortable && !isMultiSorting) && (
                <SortSelect sort={sort} onSortChange={onSortChange} sortByList={sortByList} defaultSort={defaultSort} />
              )}
              {(sortable && isMultiSorting) && (
                <MultiSortSelect
                  sort={sort}
                  onSortChange={onSortChange}
                  sortByList={sortByList}
                  defaultSort={defaultSort}
                />
              )}
              <div className='actions-side'>
                {actionsContent}
                {!autoSearch && <SearchButton onClick={() => handleSearch(true)} />}
                {allowReset && <ResetButton onClick={handleReset} />}
                {togglePosition && (
                  <Button onClick={togglePosition} icon={isOnSide ? 'layout-stacked' : 'layout-1-by-4'} />
                )}
                {hasAdvanced && (
                  <Button
                    id='advanced-filters-button'
                    className='advanced-filters-button'
                    title={showAdvanced ? 'Hide' : 'Advanced'}
                    icon='filter'
                    onClick={toggleAdvanced}
                  />
                )}
                {expanded ? (
                  <CloseButton
                    id='close-filters-button'
                    className='close-filters-button'
                    onClick={() => setExpanded(false)}
                  />
                ) : (
                  <Button
                    responsive
                    id='expand-filters-button'
                    className='expand-filters-button'
                    icon='filter'
                    onClick={() => setExpanded(true)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {filterFields?.length > 0 && (
          <div className={filterClassName}>
            <div className='filters-container'>
              {fieldsExtraStart}
              {React.Children.map(children, (child) => (
                <div className='filter-item'>{child}</div>
              ))}
              {filterFields.map(({
                name, isAdvanced, debounceTimer, ...fieldProps
              }) => (
                <>
                  <FilterComponent
                    key={name}
                    name={name}
                    value={values[name]}
                    onChange={onChangeHandler(name, debounceTimer)}
                    disabled={disabled}
                    onEnterKeyPressed={() => handleSearch()}
                    className={isAdvanced && !showAdvanced && 'hidden'}
                    {...fieldProps}
                  />
                </>
              ))}
              {fieldsExtraEnd}
            </div>
          </div>
        )}
      </div>
    </BaseHotKeys>
  );
};

GridFiltersBar.propTypes = {
  title: PropTypes.string,
  searchParams: PropTypes.object,
  onSearch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
  filterFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      hint: PropTypes.string,
      Component: PropTypes.elementType.isRequired,
      isAdvanced: PropTypes.bool,
    }),
  ),
};

GridFiltersBar.defaultProps = {
  title: '',
  searchParams: {},
  disabled: false,
  filterFields: [],
};

export default GridFiltersBar;
